/* eslint-disable @typescript-eslint/no-var-requires */
import getLang from 'I18n/utils/getLang';
import setDocumentTitle from 'I18n/utils/setDocumentTitle';
import { AppRoot, renderAppRoot } from 'quartz-core/internal/root-renderer';
import { initializeI18n } from 'quartz-i18n/initialize';
import { initializeTracking } from 'quartz-tracking/initialize';
import { getQuartzConfig } from 'quartz-config/evaluable';
import { InternalAuthError } from 'quartz-auth/internal/internalAuth';
import { VpnRequiredError } from 'quartz-auth/internal/vpnAuth';
import { invariant } from 'quartz/invariant';
import { initializeHistory } from 'quartz-routing/initialize';
import { mark } from './perf';
import { isPromiseLike } from './isPromiseLike';
import quartzCoreLang from 'i2l?sync&query=sporks!../../lang/en.lyaml';
import { Metrics } from './metrics';
import { getBasenameFromRouting } from '../util/getBasenameFromRouting';
import { renderInternalAuthErrorPage } from './InternalAuthErrorPage';
import { renderVpnRequiredErrorPage } from './VpnRequiredErrorPage';
import { renderApp as srcdocRenderApp } from './srcdoc-app-renderer';
import Raven from 'raven-js';
import { gate } from 'hub-http/gates';

/** Checks the global granite gate + the remote's specific gate. */
import { jsx as _jsx } from "react/jsx-runtime";
export function isGraniteGatingEnabled(graniteGate, gates) {
  // eslint-disable-next-line hubspot-dev/no-bare-gate-strings
  if (!gates.has(gate('FrontendFramework:Granite'))) {
    return false;
  }
  // eslint-disable-next-line hubspot-dev/no-bare-gate-strings
  return !graniteGate || gates.has(gate(graniteGate));
}
export async function renderApp({
  prepareApp,
  AppComponent,
  getLocaleInfo
}, {
  rhumbConfig,
  lang,
  container,
  LayoutComponent,
  adapter
}) {
  const quartzConfig = getQuartzConfig();
  /* invariant(quartzConfig.type === 'application') */
  ;
  const isSrcdocMode = quartzConfig.type === 'application' && quartzConfig.auth === 'none' && quartzConfig.UNSAFE_EXPERIMENTAL_srcdocMode;
  if (isSrcdocMode) {
    return srcdocRenderApp({
      prepareApp,
      AppComponent,
      getLocaleInfo
    }, {
      rhumbConfig,
      lang,
      container
    });
  }
  const {
    htmlTitleLangKey,
    historyBasename: historyBasenameFromConfig,
    UNSAFE_EXPERIMENTAL_routing: routing,
    rhumbTrackVisibility
  } = quartzConfig;
  const appBasename = historyBasenameFromConfig || getBasenameFromRouting(routing);
  let authInitResult;
  try {
    authInitResult = await adapter.initialize(new URLSearchParams(window.location.search));
  } catch (error) {
    if (error instanceof InternalAuthError) {
      Metrics.counter('internalAuth.emailCheckBlocked').increment();
      return renderInternalAuthErrorPage({
        container,
        legacyRoot: adapter.shouldUseLegacyRoot(),
        strictMode: quartzConfig.reactStrictMode
      });
    }
    if (error instanceof VpnRequiredError) {
      Metrics.counter('vpnRequired.blocked').increment();
      return renderVpnRequiredErrorPage({
        container,
        legacyRoot: adapter.shouldUseLegacyRoot(),
        strictMode: quartzConfig.reactStrictMode
      });
    }
    throw error;
  }
  const {
    userId,
    localeInfoFallback,
    gates
  } = authInitResult;

  // These checks must reference quartzConfig properties directly (not via
  // intermediate variables) so the eval-loader can statically evaluate the
  // condition and tree-shake the GraniteRuntimeHostApp require() below.
  let isGraniteEnabled = false;
  if ( /* quartzConfig.UNSAFE_EXPERIMENTAL_graniteOptions && (quartzConfig.UNSAFE_EXPERIMENTAL_graniteOptions.type === 'runtime-host' || quartzConfig.UNSAFE_EXPERIMENTAL_graniteOptions.type === 'runtime-host-and-remote') */false) {
    const graniteGate = quartzConfig.UNSAFE_EXPERIMENTAL_graniteOptions.gate;
    isGraniteEnabled = isGraniteGatingEnabled(graniteGate, gates);
    if (isGraniteEnabled) {
      Raven.setTagsContext({
        is_granite: true
      });
    }
  }
  const historyBasename = appBasename;
  if (!historyBasename) {
    throw new Error('historyBasename is required');
  }
  const history = initializeHistory(historyBasename);
  const {
    isPublicI18n,
    registerFn
  } = adapter.getI18nConfig();
  const localeInfoResult = getLocaleInfo === null || getLocaleInfo === void 0 ? void 0 : getLocaleInfo();
  let localeInfo = isPromiseLike(localeInfoResult) ? await localeInfoResult : localeInfoResult;
  if (!isPublicI18n && !localeInfo) {
    localeInfo = localeInfoFallback;
  }
  const [result] = await Promise.all([prepareApp(),
  // Warm the granite-config-worker response in parallel with i18n init so the
  // route index is present before routing decisions. Lazily required to keep
  // granite-config out of non-granite app bundles.
  isGraniteEnabled ? require('../granite/granite-config').loadGraniteConfig() : undefined, initializeI18n({
    langs: [lang, quartzCoreLang],
    createPublicProvider: isPublicI18n,
    localeInfo,
    registerFn,
    trackSetLocale: () => {
      Metrics.counter('i18n_setLocale_called').increment();
    }
  })]);
  const {
    email,
    hstc,
    hubId
  } = adapter.getTrackingProperties();
  initializeTracking({
    lang: getLang(),
    email: email !== null && email !== void 0 ? email : null,
    hstc: hstc !== null && hstc !== void 0 ? hstc : null,
    hubId
  });
  mark('quartz.prepareAppFinished');
  if (htmlTitleLangKey) {
    setDocumentTitle(htmlTitleLangKey);
  }
  let maybeRoot;
  if (result && 'redirect' in result) {
    window.location.href = result.redirect;
  } else {
    var _adapter$getCopilotOp, _result$onAfterRender;
    const copilotOptions = (_adapter$getCopilotOp = adapter.getCopilotOptions) === null || _adapter$getCopilotOp === void 0 ? void 0 : _adapter$getCopilotOp.call(adapter);
    let appContent;

    // The require() for GraniteRuntimeHostApp must live inside a quartzConfig-only
    // condition so the eval-loader evaluates the outer if to `false` for non-granite
    // apps. This prevents webpack from recording GraniteRuntimeHostApp (and its
    // module-federation runtime transitive dep) as a bundle dependency in webpack
    // builds of non-granite apps. The runtime isGraniteEnabled check is nested
    // inside, not mixed into the outer condition.
    if ( /* quartzConfig.UNSAFE_EXPERIMENTAL_graniteOptions && (quartzConfig.UNSAFE_EXPERIMENTAL_graniteOptions.type === 'runtime-host' || quartzConfig.UNSAFE_EXPERIMENTAL_graniteOptions.type === 'runtime-host-and-remote') */false) {
      if (isGraniteEnabled) {
        const {
          createGraniteRuntimeHostAppRoot
        } = require('../granite/GraniteRuntimeHostApp');
        const GraniteRuntimeHostAppRoot = createGraniteRuntimeHostAppRoot({
          prepareApp,
          initialAppState: {
            initialPrepareAppResult: result
          },
          AppComponent,
          LayoutComponent,
          rhumbConfig,
          rhumbTrackVisibility,
          userId,
          adapter,
          copilotLayoutType: copilotOptions === null || copilotOptions === void 0 ? void 0 : copilotOptions.copilotLayoutType
        });
        appContent = /*#__PURE__*/_jsx(GraniteRuntimeHostAppRoot, {
          gates: gates
        });
        maybeRoot = await renderAppRoot(appContent, Object.assign({
          legacyRoot: adapter.shouldUseLegacyRoot(),
          strictMode: quartzConfig.reactStrictMode
        }, copilotOptions, {
          container,
          isGraniteEnabled: true
        }));
      }
    }
    if (!isGraniteEnabled) {
      if (LayoutComponent) {
        appContent = /*#__PURE__*/_jsx(LayoutComponent, {
          children: /*#__PURE__*/_jsx(AppComponent, Object.assign({}, result === null || result === void 0 ? void 0 : result.props))
        });
      } else {
        appContent = /*#__PURE__*/_jsx(AppComponent, Object.assign({}, result === null || result === void 0 ? void 0 : result.props));
      }
      maybeRoot = await renderAppRoot( /*#__PURE__*/_jsx(AppRoot, {
        history: history,
        rhumbConfig: rhumbConfig,
        rhumbTrackVisibility: rhumbTrackVisibility,
        userId: userId,
        isGraniteEnabled: isGraniteEnabled,
        isGraniteRemoteApp: false,
        children: appContent
      }), Object.assign({
        legacyRoot: adapter.shouldUseLegacyRoot(),
        strictMode: quartzConfig.reactStrictMode
      }, copilotOptions, {
        container,
        isGraniteEnabled
      }));
    }
    result === null || result === void 0 || (_result$onAfterRender = result.onAfterRender) === null || _result$onAfterRender === void 0 || _result$onAfterRender.call(result);
    if ( /* 'plugins' in quartzConfig */false) {
      const {
        getAppExtensions
      } = require('./plugins/app-plugins');
      for (const plugin of getAppExtensions()) {
        plugin.hooks.onAfterRender();
      }
    }
    return maybeRoot;
  }
}